import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import '../IpPatientDetails.css';
import {
  Box, FormControl, IconButton, InputLabel, InputAdornment, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, TablePagination
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import SearchIcon from '@mui/icons-material/Search';
import { CommonPatientDetails } from '../../../../../Components/Common Components/CommonComponents';
import { getCachevalue } from '../../../../../Utility/Services/CacheProviderService';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../../../Components/Loader';
import { Colors } from '../../../../../Styles/Colors';

const MEAN_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
];

const DRUG_CHART_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'Not Started', label: 'Not Started' },
  { value: 'Started', label: 'Started' },
  { value: 'Paused', label: 'Paused' },
  { value: 'Stopped', label: 'Stopped' },
  { value: 'Completed', label: 'Completed' },
];

const DURATION_MODE_MAP = { 1: 'Day(s)', 2: 'Week(s)', 3: 'Month(s)' };

export default function IPDrugChart() {
  const location = useLocation();
  const isDoctorModule = (location.pathname || '').includes('IPDoctorHome');

  const [patientData, setPatientData] = useState({});
  const [withOutAppointment, setWithOutAppointment] = useState({});
  const [list, setList] = useState([]);
  const [meanFilter, setMeanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const ip_admission_id = patientData?.id || withOutAppointment?.ip_admission_id;

  const fetchPatient = useCallback(() => {
    const raw = getCachevalue('IppatientData');
    if (!raw) return;
    try {
      setPatientData(JSON.parse(raw));
    } catch (e) {}
    const appRaw = getCachevalue('withoutAppointment');
    if (appRaw) {
      try {
        const app = JSON.parse(appRaw);
        if (app?.patient_id === JSON.parse(raw)?.patient_id) setWithOutAppointment(app);
      } catch (e) {}
    }
  }, []);

  const fetchList = useCallback(() => {
    if (!ip_admission_id) return;
    const params = new URLSearchParams({ ip_admission_id });
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    if (meanFilter && meanFilter !== 'all') params.set('mean', meanFilter);
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_MEDICATION_DRUG_CHART}?${params.toString()}`)
      .then((res) => {
        if (res.data?.status === 'success') setList(Array.isArray(res.data?.data) ? res.data.data : []);
        else setList([]);
        setIsLoader(false);
      })
      .catch(() => { setList([]); setIsLoader(false); });
  }, [ip_admission_id, statusFilter, meanFilter]);

  useEffect(() => { fetchPatient(); }, [fetchPatient]);
  useEffect(() => { if (ip_admission_id) fetchList(); }, [ip_admission_id, fetchList]);

  const showError = (msg) => { setIsErrorMsg(true); setIsErrorMsgText(msg); };
  const showSuccess = (msg) => { setSuccessMsg(true); setSuccessMsgText(msg); };
  const msgClose = () => { setIsErrorMsg(false); setSuccessMsg(false); };

  const handleStatusChange = (prescription_detail_id, newStatus) => {
    if (!prescription_detail_id) return;
    setIsLoader(true);
    RestAPIService.create(
      { prescription_detail_id, status: newStatus },
      Serviceurls.IP_MEDICATION_DRUG_CHART
    )
      .then((res) => {
        setIsLoader(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Updated.');
          fetchList();
        } else showError(res.data?.message || 'Update failed.');
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Update failed.');
      });
  };

  const handlePrint = () => {
    if (!ip_admission_id) return;
    try {
      setIsLoader(true);
      RestAPIService.getAll(`${Serviceurls.IP_MEDICATION_DRUG_CHART}?ip_admission_id=${ip_admission_id}&export_type=pdf`)
        .then((response) => {
          const file = new Blob([response.data], { type: "application/pdf" });
          window.open(URL.createObjectURL(file));
          setIsLoader(false);
        })
        .catch((e) => showError(e?.response?.data?.message || e?.message || "Print failed"))
        .finally(() => setIsLoader(false));
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || "Print failed");
    }
  };

  const formatMaen = (row) => {
    const m = row.morning ? 1 : 0;
    const a = row.afternoon ? 1 : 0;
    const e = row.evening ? 1 : 0;
    const n = row.night ? 1 : 0;
    const dur = row.duration ? `${row.duration} ${DURATION_MODE_MAP[row.duration_mode] || 'Day(s)'}` : '';
    return `${m}-${a}-${e}-${n}${dur ? ` / ${dur}` : ''}`;
  };

  const filteredList = list.filter((row) => {
    if (!search?.trim()) return true;
    const term = String(search).toLowerCase();
    return (row.drugs || []).some(
      (d) => (d.drug_name || '').toLowerCase().includes(term) || (d.generic_name || '').toLowerCase().includes(term)
    );
  });
  const paginatedList = filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const tableCellSx = { verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' };
  const tableHeaderSx = { ...tableCellSx, fontWeight: 600, bgcolor: 'grey.100' };

  return (
    <Box sx={{ backgroundColor: Colors.configHomeBg, minHeight: '74dvh', p: '0.625vw' }}>
      <Box sx={{ mb: 2 }}>
        <CommonPatientDetails data={patientData} showDetailed={true} />
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>MEAN</InputLabel>
            <Select value={meanFilter} label="MEAN" onChange={(e) => { setMeanFilter(e.target.value); setPage(0); }}>
              {MEAN_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
              {DRUG_CHART_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 180 }}
          />
          <IconButton size="small" onClick={handlePrint} title="Print">
            <PrintIcon />
          </IconButton>
        </Box>
        <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { borderRight: '1px solid', borderColor: 'divider' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderSx}>Date & Time</TableCell>
              <TableCell sx={tableHeaderSx}>Drug Name</TableCell>
              <TableCell sx={tableHeaderSx}>Dosage Strength</TableCell>
              <TableCell sx={tableHeaderSx}>Dosage Type</TableCell>
              <TableCell sx={tableHeaderSx}>Days</TableCell>
              <TableCell sx={tableHeaderSx}>M-A-E-N</TableCell>
              <TableCell sx={tableHeaderSx}>Doctor Name</TableCell>
              <TableCell sx={tableHeaderSx}>Instruction</TableCell>
              <TableCell sx={tableHeaderSx}>Comments</TableCell>
              <TableCell sx={tableHeaderSx}>Status</TableCell>
              {isDoctorModule && <TableCell align="center" sx={{ ...tableHeaderSx, borderRight: 'none' }}>Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.map((row, rowIndex) => {
              const drugs = row.drugs || [];
              const drugCount = drugs.length || 1;
              const borderColor = rowIndex % 2 === 0 ? 'gray' : '#4da3d4';
              const prescriptionDetailId = row.prescription_detail_id ?? row.id;
              const currentStatus = row.status || 'Not Started';
              return (
                <React.Fragment key={row.prescription_id ?? row.id}>
                  {drugs.map((drug, drugIndex) => {
                    const isFirst = drugIndex === 0;
                    return (
                      <TableRow key={`${row.prescription_id}-${drug?.id ?? drugIndex}`} hover sx={{ borderLeft: `5px solid ${borderColor}` }}>
                        {isFirst && <TableCell rowSpan={drugCount} sx={tableCellSx}>{row.prescription_date || '-'}</TableCell>}
                        <TableCell sx={tableCellSx}>{drug.drug_name || drug.generic_name || '-'}</TableCell>
                        <TableCell sx={tableCellSx}>{drug.drug_strength ?? '-'}</TableCell>
                        <TableCell sx={tableCellSx}>{drug.dosage_type ?? '-'}</TableCell>
                        {isFirst && <TableCell rowSpan={drugCount} sx={tableCellSx}>{row.duration ?? '-'}</TableCell>}
                        {isFirst && <TableCell rowSpan={drugCount} sx={tableCellSx}>{formatMaen(row)}</TableCell>}
                        {isFirst && <TableCell rowSpan={drugCount} sx={tableCellSx}>{row.doctor_name || '-'}</TableCell>}
                        {isFirst && <TableCell rowSpan={drugCount} sx={tableCellSx}>{row.instructions || '-'}</TableCell>}
                        {isFirst && <TableCell rowSpan={drugCount} sx={tableCellSx}>{row.comments || '-'}</TableCell>}
                        {isFirst && <TableCell rowSpan={drugCount} sx={tableCellSx}>{currentStatus}</TableCell>}
                        {isDoctorModule && isFirst && (
                          <TableCell align="center" rowSpan={drugCount} sx={{ ...tableCellSx, borderRight: 'none' }}>
                            {(currentStatus === 'Not Started' || currentStatus === 'Stopped') && (
                              <IconButton size="small" color="primary" title="Start / Resume" onClick={() => handleStatusChange(prescriptionDetailId, 'Started')}>
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            )}
                            {currentStatus === 'Started' && (
                              <IconButton size="small" color="error" title="Stop" onClick={() => handleStatusChange(prescriptionDetailId, 'Stopped')}>
                                <StopIcon fontSize="small" />
                              </IconButton>
                            )}
                            {currentStatus === 'Completed' && (
                              <IconButton size="small" disabled title="Completed">
                                <PlayArrowIcon fontSize="small" sx={{ color: 'action.disabled' }} />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {drugs.length === 0 && (
                    <TableRow sx={{ borderLeft: `5px solid ${borderColor}` }}>
                      <TableCell sx={tableCellSx}>{row.prescription_date || '-'}</TableCell>
                      <TableCell sx={tableCellSx}>-</TableCell>
                      <TableCell sx={tableCellSx}>-</TableCell>
                      <TableCell sx={tableCellSx}>-</TableCell>
                      <TableCell sx={tableCellSx}>{row.duration ?? '-'}</TableCell>
                      <TableCell sx={tableCellSx}>{formatMaen(row)}</TableCell>
                      <TableCell sx={tableCellSx}>{row.doctor_name || '-'}</TableCell>
                      <TableCell sx={tableCellSx}>{row.instructions || '-'}</TableCell>
                      <TableCell sx={tableCellSx}>{row.comments || '-'}</TableCell>
                      <TableCell sx={tableCellSx}>{currentStatus}</TableCell>
                      {isDoctorModule && (
                        <TableCell align="center" sx={{ ...tableCellSx, borderRight: 'none' }}>
                          {(currentStatus === 'Not Started' || currentStatus === 'Paused') && (
                            <IconButton size="small" color="primary" title="Start / Resume" onClick={() => handleStatusChange(prescriptionDetailId, 'Started')}>
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          )}
                          {currentStatus === 'Started' && (
                            <IconButton size="small" color="error" title="Stop" onClick={() => handleStatusChange(prescriptionDetailId, 'Stopped')}>
                              <StopIcon fontSize="small" />
                            </IconButton>
                          )}
                          {currentStatus === 'Completed' && (
                            <IconButton size="small" disabled title="Completed">
                              <PlayArrowIcon fontSize="small" sx={{ color: 'action.disabled' }} />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredList.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Rows per page:"
        />
        {filteredList.length === 0 && !isLoader && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No records</Typography>
        )}
      </Paper>

      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}
